import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useT } from "../i18n";
import LangSwitcher from "./LangSwitcher";
import {
    ImagesSquare,
    DownloadSimple,
    QrCode,
    SignOut,
    ShieldCheck,
    User,
    Wrench,
} from "@phosphor-icons/react";

export default function Layout() {
    const { user, logout } = useAuth();
    const { t } = useT();
    const nav = useNavigate();

    const handleLogout = async () => {
        await logout();
        nav("/login", { replace: true });
    };

    const role = user?.role;
    const isAdmin = role === "admin";

    const NAV = [
        { to: "/galerie", label: t("nav_gallery"), icon: ImagesSquare, testid: "nav-galerie" },
        { to: "/descarcari", label: t("nav_downloader"), icon: DownloadSimple, testid: "nav-descarcari" },
        { to: "/qr", label: t("nav_qr"), icon: QrCode, testid: "nav-qr" },
    ];
    if (isAdmin) {
        NAV.push({ to: "/admin", label: t("nav_admin"), icon: Wrench, testid: "nav-admin" });
    }

    return (
        <div className="min-h-screen flex flex-col" style={{ background: "var(--bg)" }}>
            <div className="grain" />
            <header
                className="sticky top-0 z-30 border-b"
                style={{ background: "rgba(5,5,5,0.85)", backdropFilter: "blur(14px)", borderColor: "var(--border)" }}
            >
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
                    <div className="flex items-baseline gap-3 min-w-0">
                        <span className="heading-tight text-xl sm:text-2xl tracking-tight">db.mae</span>
                        <span className="label-mono hidden md:inline">{t("brand_tagline")}</span>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3">
                        <LangSwitcher compact />
                        <span
                            className="hidden sm:flex items-center gap-2 px-2.5 py-1 border"
                            style={{ borderColor: "var(--border)" }}
                            data-testid="badge-role"
                        >
                            {isAdmin ? <ShieldCheck size={14} /> : <User size={14} />}
                            <span className="label-mono" style={{ color: "var(--text)" }}>
                                {isAdmin ? "admin" : "guest"}
                            </span>
                        </span>
                        <button
                            onClick={handleLogout}
                            data-testid="btn-logout"
                            className="btn-press flex items-center gap-2 px-3 h-9 border text-sm"
                            style={{ borderColor: "var(--border)" }}
                        >
                            <SignOut size={16} />
                            <span className="hidden sm:inline">{t("nav_logout")}</span>
                        </button>
                    </div>
                </div>
            </header>

            <nav className="hidden sm:flex justify-center gap-2 border-b" style={{ borderColor: "var(--border)" }}>
                <div className="max-w-6xl w-full mx-auto px-6 flex gap-1">
                    {NAV.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            data-testid={`${n.testid}-desktop`}
                            className="px-4 py-3 text-sm btn-press flex items-center gap-2 -mb-px border-b-2"
                            style={({ isActive }) => ({
                                color: isActive ? "var(--text)" : "var(--text-muted)",
                                borderColor: isActive ? "var(--text)" : "transparent",
                            })}
                        >
                            <n.icon size={16} />
                            {n.label}
                        </NavLink>
                    ))}
                </div>
            </nav>

            <main className="flex-1 relative z-10 max-w-6xl w-full mx-auto px-4 sm:px-6 pb-32 sm:pb-8 pt-4 sm:pt-6">
                <Outlet />
            </main>

            <nav
                className="fixed bottom-0 left-0 right-0 z-30 border-t sm:hidden"
                style={{
                    background: "rgba(5,5,5,0.92)",
                    backdropFilter: "blur(14px)",
                    borderColor: "var(--border)",
                    paddingBottom: "env(safe-area-inset-bottom)",
                }}
            >
                <div className={`grid ${NAV.length === 4 ? "grid-cols-4" : "grid-cols-3"}`}>
                    {NAV.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            data-testid={n.testid}
                            className="flex flex-col items-center justify-center gap-1 h-16 btn-press"
                            style={({ isActive }) => ({
                                color: isActive ? "var(--text)" : "var(--text-muted)",
                                borderTop: isActive ? "2px solid var(--text)" : "2px solid transparent",
                            })}
                        >
                            <n.icon size={22} />
                            <span className="text-[11px] tracking-wide">{n.label}</span>
                        </NavLink>
                    ))}
                </div>
            </nav>
        </div>
    );
}
